/* Sticky Johnson — shared motion layer (GSAP 3)
 * One place for easing/duration tokens so every section animates the same way.
 * - Lazy-loads GSAP from cdnjs the first time something needs it (single request, cached).
 * - Honors prefers-reduced-motion: helpers resolve instantly, no GSAP download.
 * - Every helper degrades to a plain class toggle if GSAP fails to load.
 *
 * Usage:  SJMotion.ready(function(gsap){ ... })          // gsap may be null (fallback)
 *         SJMotion.modalOpen({backdrop, panel, items})   // returns Promise
 *         SJMotion.modalClose({backdrop, panel})         // returns Promise
 *         SJMotion.pop(el)                               // spring scale pulse
 *         SJMotion.reveal(items, {stagger})              // fade/rise-in list
 */
(function (w) {
  if (w.SJMotion) return;
  var GSAP_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var reduced = w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var loading = null;

  var T = {
    // brand motion tokens — flat, quick, confident. No bounce on layout, spring only on micro-feedback.
    ease: 'power3.out',
    easeIn: 'power2.in',
    easeSpring: 'back.out(2.2)',
    fast: 0.18,
    base: 0.32,
    slow: 0.5,
    stagger: 0.045,
    rise: 14 // px
  };

  function load() {
    if (reduced) return Promise.resolve(null);
    if (w.gsap) return Promise.resolve(w.gsap);
    if (loading) return loading;
    loading = new Promise(function (res) {
      var s = document.createElement('script');
      s.src = GSAP_SRC; s.async = true;
      s.onload = function () { res(w.gsap || null); };
      s.onerror = function () { res(null); };
      document.head.appendChild(s);
    });
    return loading;
  }

  function show(el) { if (el) { el.style.display = ''; el.classList.add('is-open'); } }
  function hide(el) { if (el) { el.classList.remove('is-open'); el.style.display = ''; } }

  var M = {
    tokens: T,
    reduced: reduced,
    ready: function (fn) { return load().then(function (g) { fn && fn(g); return g; }); },

    /* Modal / dialog entrance. panel scales+rises in behind a fading backdrop, then inner items stagger up. */
    modalOpen: function (o) {
      return load().then(function (g) {
        show(o.backdrop); show(o.panel);
        if (!g) return;
        g.killTweensOf([o.backdrop, o.panel].concat(o.items || []));
        var tl = g.timeline({ defaults: { ease: T.ease, overwrite: 'auto' } });
        tl.fromTo(o.backdrop, { opacity: 0 }, { opacity: 1, duration: T.base }, 0)
          .fromTo(o.panel, { opacity: 0, y: 24, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: T.slow, clearProps: 'transform' }, 0.02);
        if (o.items && o.items.length) {
          tl.fromTo(o.items, { opacity: 0, y: T.rise }, { opacity: 1, y: 0, duration: T.base, stagger: T.stagger, clearProps: 'transform,opacity' }, 0.16);
        }
        return tl.then();
      });
    },

    modalClose: function (o) {
      return load().then(function (g) {
        if (!g) { hide(o.backdrop); hide(o.panel); return; }
        g.killTweensOf([o.backdrop, o.panel].concat(o.items || []));
        var tl = g.timeline({ defaults: { ease: T.easeIn, overwrite: 'auto' } });
        tl.to(o.panel, { opacity: 0, y: 12, scale: 0.98, duration: T.fast }, 0)
          .to(o.backdrop, { opacity: 0, duration: T.fast }, 0.04);
        return tl.then(function () { hide(o.backdrop); hide(o.panel); g.set([o.panel, o.backdrop], { clearProps: 'all' }); });
      });
    },

    /* Micro-feedback: quick spring pulse (badges, counters, qty values). */
    pop: function (el, amount) {
      if (!el) return;
      load().then(function (g) {
        if (!g) return;
        g.killTweensOf(el);
        g.fromTo(el, { scale: 1 }, { scale: amount || 1.18, duration: T.fast, ease: 'power2.out', yoyo: true, repeat: 1, clearProps: 'transform' });
      });
    },

    /* Staggered rise-in for a list of elements (cards, lines of a panel). */
    reveal: function (items, opts) {
      opts = opts || {};
      return load().then(function (g) {
        if (!g || !items || !items.length) return;
        g.fromTo(items, { opacity: 0, y: opts.rise || T.rise }, { opacity: 1, y: 0, duration: opts.duration || T.base, ease: T.ease, stagger: opts.stagger || T.stagger, clearProps: 'transform,opacity', delay: opts.delay || 0 });
      });
    },

    /* Swap an image with a soft cross-fade + slight scale settle. */
    imageSwap: function (img, src, alt) {
      if (!img) return;
      load().then(function (g) {
        if (!g) { img.src = src; img.alt = alt || ''; return; }
        g.killTweensOf(img);
        g.to(img, { opacity: 0, duration: T.fast, ease: T.easeIn, onComplete: function () {
          img.src = src; img.alt = alt || '';
          g.fromTo(img, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: T.base, ease: T.ease, clearProps: 'transform' });
        } });
      });
    }
  };

  w.SJMotion = M;
})(window);
